import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './company/Header'; // Import Header component

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/cars/`;  // Change this to your actual API endpoint

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [newCar, setNewCar] = useState({
    car_brand: '',
    car_model: '',
    number_of_seats: '',
    plate_number: '',
    car_inspection_date: '',
    car_insurance_date: '',
    car_status: 'available',
    car_mileage: '',
    technical_condition: 'good',
  });
  const [editingCarId, setEditingCarId] = useState(null); // Holds the ID of the car being edited

  // Fetch vehicle data from API
  useEffect(() => {
    const fetchVehicles = async () => {
      const token = localStorage.getItem('access_token'); // Get token from localStorage
      try {
        const response = await axios.get(API_BASE_URL, {
          headers: {
            Authorization: `Bearer ${token}`,  // Send token in Authorization header
          },
        });
        setVehicles(response.data);
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    };
    fetchVehicles();
  }, []);

  // Handle input changes for the new or editing vehicle
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCar((prevCar) => ({
      ...prevCar,
      [name]: value,
    }));
  };

  // Add or update vehicle
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('access_token');
    try {
      if (editingCarId) {
        // Update vehicle
        await axios.put(`${API_BASE_URL}${editingCarId}/`, newCar, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        setVehicles(vehicles.map((car) => (car.id === editingCarId ? { ...newCar, id: editingCarId } : car)));
        setEditingCarId(null); // Reset editing state
      } else {
        // Add new vehicle
        const response = await axios.post(API_BASE_URL, newCar, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        setVehicles([...vehicles, response.data]); // Add the new vehicle to the list
      }

      setNewCar({
        car_brand: '',
        car_model: '',
        number_of_seats: '',
        plate_number: '',
        car_inspection_date: '',
        car_insurance_date: '',
        car_status: 'available',
        car_mileage: '',
        technical_condition: 'good',
      });
    } catch (error) {
      console.error('Error saving vehicle:', error);
    }
  };

  // Edit vehicle
  const handleEdit = (car) => {
    setNewCar(car);
    setEditingCarId(car.id);
  };

  // Delete vehicle
  const handleDelete = async (carId) => {
    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(`${API_BASE_URL}${carId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVehicles(vehicles.filter((car) => car.id !== carId)); // Remove car from the list
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  };

  return (
    <div>
      <Header /> {/* Include the header component */}
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Vehicles</h1>

        {/* List of vehicles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.length > 0 ? (
            vehicles.map((vehicle) => (
              <div key={vehicle.id} className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-xl font-bold mb-2">{vehicle.car_brand} {vehicle.car_model}</h2>
                <p className="text-gray-700">Seats: {vehicle.number_of_seats}</p>
                <p className="text-gray-700">Plate: {vehicle.plate_number}</p>
                <p className="text-gray-700">Status: {vehicle.car_status}</p>
                <p className="text-gray-700">Mileage: {vehicle.car_mileage}</p>
                <button
                  onClick={() => handleEdit(vehicle)}
                  className="mt-4 bg-yellow-500 text-white px-4 py-2 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(vehicle.id)}
                  className="mt-4 bg-red-500 text-white px-4 py-2 rounded ml-2"
                >
                  Delete
                </button>
              </div>
            ))
          ) : (
            <p>Loading vehicles...</p>
          )}
        </div>

        {/* Add/Edit Vehicle Form */}
        <h2 className="text-2xl font-bold mt-8">{editingCarId ? 'Edit Vehicle' : 'Add New Vehicle'}</h2>
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="car_brand"
              value={newCar.car_brand}
              onChange={handleInputChange}
              placeholder="Car Brand"
              className="border border-gray-300 p-2 rounded"
              required
            />
            <input
              type="text"
              name="car_model"
              value={newCar.car_model}
              onChange={handleInputChange}
              placeholder="Car Model"
              className="border border-gray-300 p-2 rounded"
              required
            />
            <input
              type="text"
              name="number_of_seats"
              value={newCar.number_of_seats}
              onChange={handleInputChange}
              placeholder="Number of Seats"
              className="border border-gray-300 p-2 rounded"
              required
            />
            <input
              type="text"
              name="plate_number"
              value={newCar.plate_number}
              onChange={handleInputChange}
              placeholder="Plate Number"
              className="border border-gray-300 p-2 rounded"
              required
            />
            <div>
              <label htmlFor="car_inspection_date" className="block text-gray-700">
                Car Inspection Date (Date of the last mechanical inspection)
              </label>
              <input
                type="date"
                name="car_inspection_date"
                id="car_inspection_date"
                value={newCar.car_inspection_date}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 rounded w-full"
                required
              />
            </div>
            <div>
              <label htmlFor="car_insurance_date" className="block text-gray-700">
                Car Insurance Expiration Date (When the insurance expires)
              </label>
              <input
                type="date"
                name="car_insurance_date"
                id="car_insurance_date"
                value={newCar.car_insurance_date}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 rounded w-full"
                required
              />
            </div>
            <select
              name="car_status"
              value={newCar.car_status}
              onChange={handleInputChange}
              className="border border-gray-300 p-2 rounded"
              required
            >
              <option value="available">Available</option>
              <option value="service">Service</option>
              <option value="reservation">Reservation</option>
            </select>
            <input
              type="text"
              name="car_mileage"
              value={newCar.car_mileage}
              onChange={handleInputChange}
              placeholder="Mileage"
              className="border border-gray-300 p-2 rounded"
              required
            />
            <select
              name="technical_condition"
              value={newCar.technical_condition}
              onChange={handleInputChange}
              className="border border-gray-300 p-2 rounded"
              required
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </select>
          </div>
          <button type="submit" className="mt-4 bg-blue-500 text-white px-4 py-2 rounded">
            {editingCarId ? 'Update Vehicle' : 'Add Vehicle'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Vehicles;
